'use client';

import { RadioGroup as RadioGroupPrimitive } from '@base-ui/react';
import { forwardRef, useId, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { LabelField, LabelFieldError } from '../label-field/label-field';
import { RadioChoiceCardGroupContext } from './radio-choice-card-context';

export type RadioChoiceCardGroupProps = Omit<RadioGroupPrimitive.Props, 'ref' | 'className'> & {
  className?: string;
  error?: boolean | string;
};

export const RadioChoiceCardGroup = forwardRef<HTMLDivElement, RadioChoiceCardGroupProps>(
  ({ error, id: idProp, className, ...props }, ref) => {
    const defaultId = useId();
    const id = idProp ?? defaultId;
    const errorId = `${id}-error`;

    const hasErrorMessage = typeof error === 'string';

    const contextValue = useMemo(() => ({ error: !!error }), [error]);

    return (
      <RadioChoiceCardGroupContext.Provider value={contextValue}>
        <div className="flex flex-col gap-3">
          <RadioGroupPrimitive
            ref={ref}
            id={id}
            data-slot="radio-choice-card-group"
            className={cn('relative flex flex-col gap-3', className)}
            aria-invalid={!!error}
            aria-describedby={hasErrorMessage ? errorId : undefined}
            {...props}
          />
          {hasErrorMessage && (
            <LabelField size="standard" errorId={errorId}>
              <LabelFieldError>{error}</LabelFieldError>
            </LabelField>
          )}
        </div>
      </RadioChoiceCardGroupContext.Provider>
    );
  },
);
RadioChoiceCardGroup.displayName = 'RadioChoiceCardGroup';
